import {Component, signal, computed, effect, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {EmployeeService} from '../services/employee.service';
import {EmployeeSyncStatusService} from '../services/employee-sync-status.service';
import {Employee, ReasonForAbsenceLug} from '../models/employee.model';
import {EmployeeSickLeaveModalComponent} from './employee-sick-leave-modal.component';
import {EmployeeSickLeaveFormModalComponent} from './employee-sick-leave-form-modal.component';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faStethoscope, faUserMinus, faHeartbeat, faChevronLeft, faChevronRight, faPenToSquare} from '@fortawesome/free-solid-svg-icons';
import {DatevAuthStatusService} from '../services/datev-auth-status.service';
import {Observable, forkJoin} from 'rxjs';

@Component({
    selector: 'app-employee-table',
    standalone: true,
    imports: [CommonModule, FormsModule, EmployeeSickLeaveModalComponent, EmployeeSickLeaveFormModalComponent, FontAwesomeModule],
    templateUrl: './employee-table.component.html',
    styleUrls: ['./employee-table.component.css']
})
export class EmployeeTableComponent implements OnDestroy {
    employees = this.employeeService.getEmployees();
    totalEmployees = this.employeeService.getTotalEmployees();
    loading = this.employeeService.getLoading();
    selectedEmployee = signal<Employee | null>(null);
    selectedEmployeeForSickLeave = signal<Employee | null>(null);
    searchTerm = signal<string>('');
    currentPage = signal<number>(0);
    pageSize = signal<number>(25);

    totalPages = computed(() => Math.max(1, Math.ceil(this.totalEmployees() / this.pageSize())));

    filteredEmployees = computed(() => {
        const term = this.searchTerm().toLowerCase().trim();
        const allEmployees = this.employees();

        if (!term) {
            return allEmployees;
        }

        return allEmployees.filter(employee =>
            employee.datevEmployeeNumber.toLowerCase().includes(term) ||
            employee.name.toLowerCase().includes(term) ||
            employee.email.toLowerCase().includes(term)
        );
    });

    medicalIcon = faStethoscope;
    sickLeaveIcon = faUserMinus;
    healthyIcon = faHeartbeat;
    prevIcon = faChevronLeft;
    nextIcon = faChevronRight;
    editIcon = faPenToSquare;

    editingEmployee = signal<Employee | null>(null);
    editEmail = signal<string>('');
    editReasonForAbsence = signal<string>('');
    editLoading = signal<boolean>(false);
    reasonCodes = signal<ReasonForAbsenceLug[]>([]);

    // Mitarbeiternummer, für die gerade ein "Erneut versuchen" der Email-DATEV-Synchronisierung läuft.
    retryingEmailSyncNumber = signal<string | null>(null);

    private emailSyncPollIntervalId: ReturnType<typeof setInterval> | null = null;
    private readonly EMAIL_SYNC_POLL_INTERVAL_MS = 3000;
    private readonly EMAIL_SYNC_POLL_MAX_ATTEMPTS = 10; // ~30s bei 3s Intervall

    // Status des vollen Mitarbeiter-Abgleichs (siehe Backend ProcessEmployeeFullSync) - wird per
    // WebSocket-Push von EmployeeSyncStatusService aktuell gehalten, kein eigenes Polling mehr
    // nötig (siehe effect() im Konstruktor für das Nachladen der Liste bei Abschluss).
    employeeSyncStatus = this.employeeSyncStatusService.getStatus();
    private syncingEmployeesNow = signal<boolean>(false);
    private readonly EMPLOYEE_SYNC_TERMINAL_STATES = ['SUCCESSFUL', 'COMPLETED', 'FAILED'];
    // Revisionsstand von EmployeeSyncStatusService zum Zeitpunkt des letzten eigenen
    // syncEmployees()-Aufrufs - erst eine Aktualisierung MIT höherer Revision gilt als Antwort
    // auf genau diesen Sync (siehe effect() im Konstruktor und syncEmployees() weiter unten).
    private syncTriggerRevision: number | null = null;

    isSyncingEmployees = computed(() => {
        if (this.syncingEmployeesNow()) {
            return true;
        }
        const state = this.employeeSyncStatus().state;
        return state !== null && !this.EMPLOYEE_SYNC_TERMINAL_STATES.includes(state);
    });

    employeeSyncStatusText = computed(() => {
        const status = this.employeeSyncStatus();
        if (status.state === null) {
            return 'Noch nie synchronisiert';
        }
        if (status.state === 'FAILED') {
            return 'Letzte Synchronisierung fehlgeschlagen';
        }
        if (this.isSyncingEmployees()) {
            return 'Synchronisierung läuft...';
        }
        return `Zuletzt synchronisiert: ${this.formatSyncTimestamp(status.startedAt)}`;
    });

    private formatSyncTimestamp(timestamp: string | null): string {
        if (!timestamp) {
            return '-';
        }
        const date = new Date(timestamp.replace(' ', 'T'));
        if (isNaN(date.getTime())) {
            return timestamp;
        }
        return date.toLocaleString('de-DE');
    }

    readonly workingDayLabels: Record<string, string> = {
        monday: 'Mo',
        tuesday: 'Di',
        wednesday: 'Mi',
        thursday: 'Do',
        friday: 'Fr',
        saturday: 'Sa',
        sunday: 'So'
    };

    readonly allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    readonly dayHoursKey: Record<string, keyof Employee> = {
        monday: 'allocation_of_working_hours_monday',
        tuesday: 'allocation_of_working_hours_tuesday',
        wednesday: 'allocation_of_working_hours_wednesday',
        thursday: 'allocation_of_working_hours_thursday',
        friday: 'allocation_of_working_hours_friday',
        saturday: 'allocation_of_working_hours_saturday',
        sunday: 'allocation_of_working_hours_sunday'
    };

    getHoursForDay(employee: Employee, day: string): number {
        return (employee[this.dayHoursKey[day]] as number) ?? 0;
    }

    constructor(
        private employeeService: EmployeeService,
        private employeeSyncStatusService: EmployeeSyncStatusService,
        public datevAuthService: DatevAuthStatusService
    ) {
        this.loadPage();
        this.employeeService.getReasonCodes().subscribe({
            next: (codes) => this.reasonCodes.set(codes)
        });

        // reagiert auf den WebSocket-Push von EmployeeSyncStatusService: sobald NACH dem
        // eigenen syncEmployees()-Aufruf eine neue Aktualisierung mit Endzustand eintrifft,
        // Liste neu laden und den Button wieder freigeben. Entscheidend ist der
        // Revisionsvergleich (> syncTriggerRevision), NICHT ein Wertevergleich des Status: zwei
        // aufeinanderfolgende Syncs enden beide typischerweise mit demselben state
        // ("COMPLETED", nur startedAt unterscheidet sich) - ein reiner
        // "hat sich state geändert"-Vergleich hätte den zweiten Abschluss in derselben Sitzung
        // nie erkannt. Live im Browser verifiziert: mit einem reinen Wertevergleich blieb genau
        // dieser zweite Sync fälschlich auf "läuft..." hängen, obwohl der Push korrekt ankam.
        effect(() => {
            const status = this.employeeSyncStatus();
            const revision = this.employeeSyncStatusService.getRevision()();
            const isNewUpdateSinceTrigger = this.syncTriggerRevision !== null && revision > this.syncTriggerRevision;

            if (this.syncingEmployeesNow() && isNewUpdateSinceTrigger && status.state !== null && this.EMPLOYEE_SYNC_TERMINAL_STATES.includes(status.state)) {
                this.syncingEmployeesNow.set(false);
                this.syncTriggerRevision = null;
                this.loadPage();
            }
        }, {allowSignalWrites: true});
    }

    private loadPage() {
        this.employeeService.loadEmployees(this.currentPage(), this.pageSize());
    }

    filterEmployees() {
        // Client-side filtering on current page
    }

    clearSearch() {
        this.searchTerm.set('');
    }

    nextPage() {
        if (this.currentPage() < this.totalPages() - 1) {
            this.currentPage.update(p => p + 1);
            this.loadPage();
        }
    }

    prevPage() {
        if (this.currentPage() > 0) {
            this.currentPage.update(p => p - 1);
            this.loadPage();
        }
    }

    goToPage(page: number) {
        if (page >= 0 && page < this.totalPages()) {
            this.currentPage.set(page);
            this.loadPage();
        }
    }

    onPageSizeChange(newSize: number) {
        this.pageSize.set(newSize);
        this.currentPage.set(0);
        this.loadPage();
    }

    showEmployeeSickLeaves(employee: Employee) {
        this.selectedEmployee.set(employee);
    }

    closeEmployeeSickLeaves() {
        this.selectedEmployee.set(null);
        this.loadPage();
    }

    showSickLeaveForm(employee: Employee) {
        if (!this.datevAuthService.getAuthStatus()()) {
            return;
        }
        this.selectedEmployeeForSickLeave.set(employee);
    }

    closeSickLeaveForm() {
        this.selectedEmployeeForSickLeave.set(null);
        this.loadPage();
    }

    startEdit(employee: Employee) {
        this.editingEmployee.set(employee);
        this.editEmail.set(employee.email);
        this.editReasonForAbsence.set(employee.reason_for_absence ?? 'K');
    }

    cancelEdit() {
        this.editingEmployee.set(null);
    }

    saveEdit() {
        const employee = this.editingEmployee();
        const email = this.editEmail().trim();
        const reason = this.editReasonForAbsence();
        if (!employee || !email) return;

        this.editLoading.set(true);

        const emailChanged = email !== employee.email;
        const reasonChanged = reason !== (employee.reason_for_absence ?? 'K');

        const updates: Observable<any>[] = [];
        if (emailChanged) {
            updates.push(this.employeeService.updateEmployeeEmail(employee.datevEmployeeNumber, email));
        }
        if (reasonChanged) {
            updates.push(this.employeeService.updateReasonForAbsence(employee.datevEmployeeNumber, reason));
        }

        if (updates.length === 0) {
            this.editLoading.set(false);
            this.editingEmployee.set(null);
            return;
        }

        forkJoin(updates).subscribe({
            next: () => {
                this.editLoading.set(false);
                this.editingEmployee.set(null);
                this.loadPage();
                if (emailChanged) {
                    // Die Email-DATEV-Synchronisierung läuft asynchron im Backend (Cron, wenige
                    // Sekunden) ohne Push-Mechanismus zum Frontend - daher kurz pollen, bis der
                    // Status nicht mehr PENDING ist.
                    this.startEmailSyncPolling(employee.datevEmployeeNumber);
                }
            },
            error: () => {
                this.editLoading.set(false);
            }
        });
    }

    isRetryingEmailSync(employee: Employee): boolean {
        return this.retryingEmailSyncNumber() === employee.datevEmployeeNumber;
    }

    retryEmailSync(employee: Employee) {
        this.retryingEmailSyncNumber.set(employee.datevEmployeeNumber);

        this.employeeService.retryEmailSync(employee.datevEmployeeNumber).subscribe({
            next: () => {
                this.retryingEmailSyncNumber.set(null);
                this.loadPage();
                this.startEmailSyncPolling(employee.datevEmployeeNumber);
            },
            error: () => {
                this.retryingEmailSyncNumber.set(null);
            }
        });
    }

    // Kein Push-Mechanismus vom Backend für den asynchronen Email-DATEV-Sync-Job - daher
    // einfaches Polling alle 3s, bis der Status des betroffenen Mitarbeiters nicht mehr
    // PENDING ist, oder nach ~30s (10 Versuchen) aufgegeben wird. Bewusst refreshEmployeesInPlace()
    // statt loadPage()/loadEmployees(): Letzteres ersetzt das komplette employees-Array und
    // toggelt loading(), was bei jedem 3s-Tick die ganze Tabelle neu aufbaut und die Liste
    // (inkl. Scroll-Position) zum oberen Rand zurückspringen lässt - nur die Zeile des
    // betroffenen Mitarbeiters soll sich aktualisieren.
    private startEmailSyncPolling(employeeNumber: string) {
        this.stopEmailSyncPolling();
        let attempts = 0;

        this.emailSyncPollIntervalId = setInterval(() => {
            attempts++;
            const current = this.employees().find(e => e.datevEmployeeNumber === employeeNumber);
            const stillPending = current ? current.email_sync_status === 'PENDING' : false;

            if (!stillPending || attempts >= this.EMAIL_SYNC_POLL_MAX_ATTEMPTS) {
                this.stopEmailSyncPolling();
                return;
            }

            this.employeeService.refreshEmployeesInPlace(this.currentPage(), this.pageSize());
        }, this.EMAIL_SYNC_POLL_INTERVAL_MS);
    }

    private stopEmailSyncPolling() {
        if (this.emailSyncPollIntervalId !== null) {
            clearInterval(this.emailSyncPollIntervalId);
            this.emailSyncPollIntervalId = null;
        }
    }

    syncEmployees() {
        this.syncingEmployeesNow.set(true);
        // merkt sich den Revisionsstand JETZT - nur eine Aktualisierung, die danach eintrifft
        // (revision > syncTriggerRevision), gilt im effect() oben als Antwort auf diesen Sync.
        this.syncTriggerRevision = this.employeeSyncStatusService.getRevision()();

        // syncingEmployeesNow bleibt bewusst true, bis der EmployeeSyncStatusService per
        // WebSocket-Push (oder dessen Polling-Fallback) einen Endzustand liefert - siehe
        // effect() im Konstruktor. Der Job selbst läuft im Backend noch Minuten weiter, der
        // POST-Erfolg hier bedeutet nur "angenommen", nicht "fertig".
        this.employeeService.syncEmployees().subscribe({
            error: () => {
                this.syncingEmployeesNow.set(false);
                this.syncTriggerRevision = null;
            }
        });
    }

    ngOnDestroy() {
        this.stopEmailSyncPolling();
    }
}
